import React, { useState } from 'react';
import './BulkImport.css';

// Content classification keywords for irrelevant data detection
const advertisementKeywords = [
  'sponsored', 'advertisement', 'promoted', 'partner', 'affiliate',
  'brought to you by', 'in partnership with', 'sponsored by',
  'ad', 'promo', 'promotion', 'offer', 'deal', 'discount'
];

const socialMediaKeywords = [
  'follow us on', 'like us on', 'share this', 'tweet about',
  'instagram', 'facebook', 'twitter', 'linkedin', 'youtube',
  'tiktok', 'snapchat', 'pinterest', 'social media'
];

const navigationKeywords = [
  'click here', 'learn more', 'read more', 'view all',
  'see more', 'find out more', 'discover more', 'explore',
  'browse', 'check out', 'visit our', 'go to'
];

const promotionalKeywords = [
  'sign up for', 'subscribe to', 'join our', 'become a member',
  'get updates', 'stay informed', 'newsletter', 'email list',
  'notifications', 'alerts', 'reminders'
];

const legalKeywords = [
  'terms of service', 'privacy policy', 'cookie policy', 'disclaimer',
  'copyright', 'all rights reserved', 'legal notice', 'terms and conditions'
];

// Helper function to classify content as relevant or irrelevant
const classifyContent = (line) => {
  const lineLower = line.toLowerCase().trim();
  
  // Skip empty lines
  if (!lineLower) return { category: 'skip', type: 'empty' };
  
  // Don't classify event descriptions as advertisements - these are legitimate content
  const eventDescriptionKeywords = [
    'believe it or not', 'join us as we learn', 'we will also tour',
    'highlights will include', 'note:', 'time is based on', 'zoom links',
    'salem witch trials', 'historical burial ground', 'memorial',
    'insane asylum', 'boston', 'puritan new england', 'hysteria and suspicion',
    'brief period of time', 'unpleasant', 'perfect for this time of year',
    'backstory', 'supposed witches', 'trial', 'outcome', 'remembered',
    'modern town', 'compare', '21st century', 'historic', 'legendary',
    'landmarks', 'burial ground', 'memorial added', 'location where things began',
    'twelve miles north', 'zoom links to attend', 'email you register',
    'eastern standard time', 'completion of the event', 'temporary recording'
  ];
  
  if (eventDescriptionKeywords.some(keyword => lineLower.includes(keyword))) {
    return { category: 'relevant', type: 'content' };
  }
  
  // Advertisement detection - be more specific
  if (advertisementKeywords.some(keyword => lineLower.includes(keyword)) && 
      !lineLower.includes('salem') && 
      !lineLower.includes('witch') &&
      !lineLower.includes('tour')) {
    return { category: 'irrelevant', type: 'advertisement' };
  }
  
  // Social media detection
  if (socialMediaKeywords.some(keyword => lineLower.includes(keyword))) {
    return { category: 'irrelevant', type: 'socialMedia' };
  }
  
  // Navigation elements
  if (navigationKeywords.some(keyword => lineLower.includes(keyword))) {
    return { category: 'irrelevant', type: 'navigation' };
  }
  
  // Promotional material
  if (promotionalKeywords.some(keyword => lineLower.includes(keyword))) {
    return { category: 'irrelevant', type: 'promotional' };
  }
  
  // Legal/policy content
  if (legalKeywords.some(keyword => lineLower.includes(keyword))) {
    return { category: 'irrelevant', type: 'legal' };
  }
  
  // Unrelated event detection (different event names, different dates)
  if (isUnrelatedEvent(line)) {
    return { category: 'irrelevant', type: 'unrelatedEvent' };
  }
  
  return { category: 'relevant', type: 'content' };
};

// Helper function to detect unrelated events
const isUnrelatedEvent = (line) => {
  const lineLower = line.toLowerCase();
  
  // Look for patterns that suggest different events
  const unrelatedPatterns = [
    /also happening/i,
    /other events/i,
    /upcoming events/i,
    /more events/i,
    /next event/i,
    /previous event/i,
    /last week/i,
    /next week/i,
    /tomorrow/i,
    /yesterday/i
  ];
  
  return unrelatedPatterns.some(pattern => pattern.test(lineLower));
};

// Helper function to combine all description data with fallback logic
const combineDescriptionData = (description, additional, price, misc, unclassified) => {
  let finalDesc = description.join(' ');
  
  if (additional.length > 0) {
    finalDesc += '\n\nAdditional Information:\n' + additional.join('\n');
  }
  
  if (price) {
    finalDesc += `\n\nCost: $${price}`;
  }
  
  if (misc.length > 0 || unclassified.length > 0) {
    finalDesc += '\n\nAdditional Details:\n';
    [...misc, ...unclassified].forEach(item => {
      if (item.trim() && !item.includes('http') && item.length > 10) {
        finalDesc += `• ${item.trim()}\n`;
      }
    });
  }
  
  return finalDesc.trim();
};

const BulkImport = ({ onImport, onCancel }) => {
  const [csvData, setCsvData] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [inputMethod, setInputMethod] = useState('text'); // 'text' or 'file'
  const [isAutoFormatting, setIsAutoFormatting] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvData(event.target.result);
        parseCSV(event.target.result);
      };
      reader.readAsText(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handleTextInput = (e) => {
    const text = e.target.value;
    setCsvData(text);
    
    // Clear any existing timeout
    if (window.autoFormatTimeout) {
      clearTimeout(window.autoFormatTimeout);
    }
    
    if (text.trim()) {
      setIsAutoFormatting(true);
      // Increased debounce time to prevent multiple processing
      window.autoFormatTimeout = setTimeout(() => {
        console.log('🔄 Processing text input after debounce...');
        // Try to auto-format the input if it's not already CSV
        const formattedText = autoFormatEventData(text);
        parseCSV(formattedText);
        setIsAutoFormatting(false);
      }, 2000); // Wait 2 seconds after user stops typing
    } else {
      setPreview([]);
      setErrors([]);
      setIsAutoFormatting(false);
    }
  };

  const handleInputMethodChange = (method) => {
    setInputMethod(method);
    setCsvData('');
    setPreview([]);
    setErrors([]);
  };

  // Auto-format raw event data into CSV format
  const autoFormatEventData = (rawText) => {
    // Check if it's already CSV format
    if (rawText.includes('event_title,description,venue,address')) {
      return rawText;
    }

    console.log('🔍 Auto-formatting input:', rawText.substring(0, 200) + '...');

    // For single event data, treat the entire text as one block
    const eventBlocks = [rawText.trim()];
    
    if (eventBlocks.length === 0) {
      return rawText;
    }

    const csvRows = [];
    
    // Add CSV header
    csvRows.push('event_title,description,venue,address,latitude,longitude,category,subcategory,start_time,end_time,format,region,coverArtUrl,isReservationsNeeded,typicalHours,isFlexible');

    eventBlocks.forEach(block => {
      const event = parseEventBlock(block);
      console.log('📝 Parsed event:', event);
      if (event && event.event_title) {
        csvRows.push(formatEventAsCSV(event));
      }
    });

    const result = csvRows.join('\n');
    console.log('✅ Auto-formatted result:', result.substring(0, 300) + '...');
    return result;
  };

  // Parse a single event block into structured data
  const parseEventBlock = (block) => {
    const lines = block.split('\n').map(line => line.trim()).filter(line => line);
    
    console.log('🔍 Parsing lines:', lines);
    
    if (lines.length === 0) return null;

    const event = {
      event_title: '',
      description: '',
      venue: '',
      address: '',
      latitude: '',
      longitude: '',
      category: '',
      subcategory: '',
      start_time: '',
      end_time: '',
      format: 'In-person',
      region: 'DC',
      coverArtUrl: '',
      isReservationsNeeded: false,
      typicalHours: '',
      isFlexible: false,
      adminNotes: {
        advertisement: [],
        socialMedia: [],
        navigation: [],
        promotional: [],
        legal: [],
        unrelatedEvents: [],
        other: []
      }
    };

    let descriptionLines = [];
    let additionalInfo = [];
    let miscellaneousData = [];
    let unclassifiedLines = [];
    let inDescription = false;
    let foundTitle = false;
    let foundDate = false;
    let price = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      console.log(`📄 Processing line ${i}: "${line}"`);
      let lineWasClassified = false;
      
      // Classify content first
      const classification = classifyContent(line);
      console.log(`🏷️ Content classification: ${classification.category} (${classification.type})`);
      
      // Handle irrelevant content
      if (classification.category === 'irrelevant') {
        const noteType = classification.type === 'advertisement' ? 'advertisement' :
                        classification.type === 'socialMedia' ? 'socialMedia' :
                        classification.type === 'navigation' ? 'navigation' :
                        classification.type === 'promotional' ? 'promotional' :
                        classification.type === 'legal' ? 'legal' :
                        classification.type === 'unrelatedEvent' ? 'unrelatedEvents' : 'other';
        
        event.adminNotes[noteType].push(line);
        lineWasClassified = true;
        console.log(`🚫 Moved to admin notes (${noteType}): "${line}"`);
        continue;
      }
      
      // Skip empty lines
      if (classification.category === 'skip') {
        lineWasClassified = true;
        continue;
      }
      
      // Skip URLs
      if (line.startsWith('http')) {
        console.log('⏭️ Skipping URL');
        lineWasClassified = true;
        continue;
      }
      
      // Handle section markers - be more flexible with detection
      if (line.toLowerCase().includes('details')) {
        inDescription = true;
        console.log('📝 Starting description section');
        lineWasClassified = true;
        continue;
      }
      
      if (['BUY NOW', 'Map', 'Suggested Attire'].includes(line)) {
        inDescription = false;
        console.log(`⏭️ Ending description section at ${line}`);
        lineWasClassified = true;
        continue;
      }

      // Extract event title (look for lines that look like titles)
      if (!foundTitle && line.length > 10 && !line.includes('@') && !line.includes('$') && !line.includes('PM') && !line.includes('AM') && !line.includes('http')) {
        // Skip lines that are clearly not titles
        if (!['Online', 'Details', 'BUY NOW', 'Map', 'Suggested Attire'].includes(line)) {
          event.event_title = line.replace(/^#+\s*/, '').trim();
          foundTitle = true;
          lineWasClassified = true;
          console.log(`✅ Found title: "${event.event_title}"`);
          continue;
        }
      }

      // Extract date/time information
      if (line.includes('@') && (line.includes('PM') || line.includes('AM'))) {
        console.log('📅 Processing date line:', line);
        const timeMatch = line.match(/(\w+,?\s+\w+\s+\d+,\s+\d+)\s+@\s+(\d+:\d+\w+)\s+To\s+(\w+,?\s+\w+\s+\d+,\s+\d+)\s+@\s+(\d+:\d+\w+)/);
        if (timeMatch) {
          event.start_time = formatDateTime(timeMatch[1], timeMatch[2]);
          event.end_time = formatDateTime(timeMatch[3], timeMatch[4]);
          foundDate = true;
          lineWasClassified = true;
          console.log(`✅ Found date range: ${event.start_time} to ${event.end_time}`);
        } else {
          const singleTimeMatch = line.match(/(\w+,?\s+\w+\s+\d+,\s+\d+)\s+@\s+(\d+:\d+\w+)/);
          if (singleTimeMatch) {
            event.start_time = formatDateTime(singleTimeMatch[1], singleTimeMatch[2]);
            foundDate = true;
            lineWasClassified = true;
            console.log(`✅ Found single date: ${event.start_time}`);
          }
        }
        continue;
      }

      // Extract venue and address for online events
      if (line === 'Online' || line.includes('Online')) {
        event.venue = 'Online Platform';
        event.address = 'Online Event';
        event.format = 'Online';
        lineWasClassified = true;
        console.log('✅ Found online event');
        continue;
      }

      // Extract price
      if (line.startsWith('$')) {
        price = line.replace('$', '').trim();
        event.isReservationsNeeded = true;
        lineWasClassified = true;
        console.log(`✅ Found price: $${price}`);
        continue;
      }

      // Extract venue (look for substantial lines that aren't titles, dates, or prices)
      if (!event.venue && line.length > 5 && !line.includes('$') && !line.includes('@') && !line.includes('http') && line !== 'Online') {
        // Skip if it looks like a title we already processed
        if (line !== event.event_title) {
          event.venue = line;
          lineWasClassified = true;
          console.log(`✅ Found venue: "${event.venue}"`);
          continue;
        }
      }

      // Extract address (usually follows venue and contains commas)
      if (event.venue && !event.address && line.length > 10 && line.includes(',')) {
        event.address = line;
        lineWasClassified = true;
        console.log(`✅ Found address: "${event.address}"`);
        continue;
      }

      // Handle Suggested Attire
      if (line === 'Suggested Attire' && i + 1 < lines.length) {
        const attire = lines[i + 1];
        additionalInfo.push(`Suggested Attire: ${attire}`);
        lineWasClassified = true;
        console.log(`✅ Found attire: ${attire}`);
        i++; // Skip the next line since we processed it
        continue;
      }

      // Collect description lines - be more aggressive about capturing content
      if (inDescription && line.length > 0) {
        descriptionLines.push(line);
        lineWasClassified = true;
        console.log(`📝 Added to description: "${line}"`);
      } else if (!foundTitle && !foundDate && line.length > 20 && !line.includes('$') && !line.includes('@') && !line.includes('http') && !['Online', 'BUY NOW', 'Map', 'Suggested Attire'].includes(line)) {
        // Also capture substantial content that might be description even if not in "Details" section
        descriptionLines.push(line);
        lineWasClassified = true;
        console.log(`📝 Added to description (fallback): "${line}"`);
      }
      
      // Track unclassified lines for fallback description
      if (!lineWasClassified && line.trim().length > 0) {
        unclassifiedLines.push(line.trim());
        console.log(`📋 Unclassified line: "${line.trim()}"`);
      }
    }

    // Combine description with enhanced fallback logic
    let fullDescription = combineDescriptionData(descriptionLines, additionalInfo, price, miscellaneousData, unclassifiedLines);
    event.description = fullDescription.trim().substring(0, 1000);
    console.log(`📝 Final description: "${event.description.substring(0, 100)}..."`);

    // Enhanced auto-categorization
    const titleLower = event.event_title.toLowerCase();
    const descLower = event.description.toLowerCase();
    
    if (titleLower.includes('embassy') || descLower.includes('embassy')) {
      event.category = 'Embassy & Culture';
      event.subcategory = 'Embassy Events';
    } else if (titleLower.includes('tour') || descLower.includes('tour')) {
      if (event.format === 'Online') {
        event.category = 'Virtual Events';
        event.subcategory = 'Online Tours';
      } else {
        event.category = 'Guided Tours';
        event.subcategory = 'Walking Tours';
      }
    } else if (titleLower.includes('halloween') || descLower.includes('halloween')) {
      event.category = 'Seasonal & Holiday Activities';
      event.subcategory = 'Halloween';
    } else if (event.format === 'Online') {
      event.category = 'Virtual Events';
      event.subcategory = 'Online Events';
    } else if (titleLower.includes('party') || titleLower.includes('nightlife')) {
      event.category = 'Nightlife & Parties';
      event.subcategory = 'Parties';
    } else {
      event.category = 'Arts/Culture';
      event.subcategory = 'Cultural Events';
    }

    console.log(`🏷️ Categorized as: ${event.category} > ${event.subcategory}`);

    return event;
  };

  // Format a single event as CSV row
  const formatEventAsCSV = (event) => {
    const csvRow = [
      `"${event.event_title}"`,
      `"${event.description}"`,
      `"${event.venue}"`,
      `"${event.address}"`,
      event.latitude,
      event.longitude,
      `"${event.category}"`,
      `"${event.subcategory}"`,
      `"${event.start_time}"`,
      `"${event.end_time}"`,
      `"${event.format}"`,
      `"${event.region}"`,
      `"${event.coverArtUrl}"`,
      event.isReservationsNeeded,
      `"${event.typicalHours}"`,
      event.isFlexible
    ].join(',');
    
    console.log('📝 CSV Row generated:', csvRow.substring(0, 200) + '...');
    return csvRow;
  };

  // Format date and time
  const formatDateTime = (dateStr, timeStr) => {
    try {
      const date = new Date(dateStr);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      
      // Format time
      let time = timeStr;
      if (!time.includes(' ')) {
        time = time.replace(/(\d+):(\d+)([AP]M)/, '$1:$2 $3');
      }
      
      return `${month} ${day}, ${year} • ${time}`;
    } catch (e) {
      return `${dateStr} • ${timeStr}`;
    }
  };

  const parseCSV = (csvText) => {
    console.log('🔄 Starting CSV parsing...');
    const lines = csvText.split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      console.log('⚠️ No lines to parse');
      setPreview([]);
      setErrors([]);
      return;
    }
    
    console.log(`📄 Processing ${lines.length} lines`);
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    console.log('📋 Headers:', headers);
    
    const events = [];
    const newErrors = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) {
        console.log(`⏭️ Skipping empty line ${i + 1}`);
        continue;
      }
      
      const values = parseCSVLine(line);
      const event = {};
      
      headers.forEach((header, index) => {
        event[header] = values[index] ? values[index].trim().replace(/"/g, '') : '';
      });
      
      // Skip rows that don't have an event title (likely empty rows)
      if (!event.event_title || event.event_title === '') {
        console.log(`⏭️ Skipping row ${i + 1} - no event title`);
        continue;
      }
      
      // Debug the parsed event
      console.log(`🔍 Validating event ${i}:`, {
        title: event.event_title,
        description: event.description?.substring(0, 50) + '...',
        venue: event.venue,
        address: event.address,
        category: event.category
      });
      
      // Validate required fields
      if (!event.event_title || event.event_title.trim() === '') {
        newErrors.push(`Row ${i + 1}: Event title is required`);
      }
      if (!event.description || event.description.trim() === '') {
        newErrors.push(`Row ${i + 1}: Description is required`);
      }
      if (!event.venue || event.venue.trim() === '') {
        newErrors.push(`Row ${i + 1}: Venue is required`);
      }
      if (!event.address || event.address.trim() === '') {
        newErrors.push(`Row ${i + 1}: Address is required`);
      }
      if (!event.category || event.category.trim() === '') {
        newErrors.push(`Row ${i + 1}: Category is required`);
      }
      
      // Convert coordinates to numbers
      if (event.latitude) {
        event.latitude = parseFloat(event.latitude);
        if (isNaN(event.latitude)) {
          newErrors.push(`Row ${i + 1}: Invalid latitude`);
        }
      }
      if (event.longitude) {
        event.longitude = parseFloat(event.longitude);
        if (isNaN(event.longitude)) {
          newErrors.push(`Row ${i + 1}: Invalid longitude`);
        }
      }
      
      // Convert boolean fields
      event.isReservationsNeeded = event.isReservationsNeeded === 'true';
      event.isFlexible = event.isFlexible === 'true';
      
      // Generate ID
      event.id = `bulk-import-${Date.now()}-${events.length}`;
      
      events.push(event);
    }
    
    console.log(`✅ Parsed ${events.length} events with ${newErrors.length} errors`);
    setPreview(events);
    setErrors(newErrors);
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  };

  const handleImport = async () => {
    if (errors.length > 0) {
      alert(`Please fix ${errors.length} errors before importing`);
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onImport(preview);
      alert(`Successfully imported ${preview.length} events!`);
    } catch (error) {
      console.error('Import error:', error);
      alert('Error importing events. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const template = `event_title,description,venue,address,latitude,longitude,category,subcategory,start_time,end_time,format,region,coverArtUrl,isReservationsNeeded,typicalHours,isFlexible
"Sample Event","This is a sample event description","Sample Venue","123 Sample St, Washington, DC 20001",38.9072,-77.0369,"Professional","Conference","Oct 30, 2024 • 9:00 AM","Oct 30, 2024 • 5:00 PM","In-person","DC","https://example.com/image.jpg",true,"9 AM - 5 PM",false
"Another Event","Another sample event","Another Venue","456 Another Ave, New York, NY 10001",40.7128,-74.0060,"Arts/Culture","Exhibition","Nov 1, 2024 • 6:00 PM","Nov 1, 2024 • 9:00 PM","In-person","NYC","https://example.com/image2.jpg",false,"6 PM - 9 PM",true`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'event_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bulk-import-overlay">
      <div className="bulk-import-modal">
        <div className="import-header">
          <h2>📊 Bulk Import Events</h2>
          <button onClick={onCancel} className="close-btn">×</button>
        </div>

        <div className="import-content">
          <div className="import-instructions">
            <h3>📋 Instructions</h3>
            <ol>
              <li>Download the CSV template below</li>
              <li>Fill in your event data (required fields: event_title, description, venue, address, category)</li>
              <li>Choose input method: paste text directly or upload CSV file</li>
              <li>Review the preview and fix any errors</li>
              <li>Click "Import Events" to add them to your database</li>
            </ol>
          </div>

          <div className="import-actions">
            <button onClick={downloadTemplate} className="btn-secondary">
              📥 Download Template
            </button>
          </div>

          <div className="input-method-selector">
            <h4>Choose Input Method</h4>
            <div className="method-tabs">
              <button 
                className={`method-tab ${inputMethod === 'text' ? 'active' : ''}`}
                onClick={() => handleInputMethodChange('text')}
              >
                📝 Paste Text
              </button>
              <button 
                className={`method-tab ${inputMethod === 'file' ? 'active' : ''}`}
                onClick={() => handleInputMethodChange('file')}
              >
                📤 Upload File
              </button>
            </div>
          </div>

          {inputMethod === 'text' ? (
            <div className="text-input-section">
              <h4>Paste Event Data (Auto-Formatted)</h4>
              <textarea
                value={csvData}
                onChange={handleTextInput}
                placeholder="Paste your event data in ANY format...&#10;&#10;Examples:&#10;• Website URLs with event info&#10;• Raw text with line breaks&#10;• Already formatted CSV data&#10;• Multiple events at once&#10;&#10;The system will automatically format everything for you!"
                className="csv-textarea"
                rows="12"
              />
              <div className="text-input-help">
                <p>🤖 <strong>Auto-Formatting:</strong> Paste event data in any format - the system will automatically convert it to proper CSV format, extract all fields, and categorize events intelligently.</p>
                <p>✅ <strong>Supports:</strong> Website data, raw text, line breaks, multiple events, URLs, and more!</p>
                {isAutoFormatting && (
                  <div className="auto-formatting-indicator">
                    <span className="spinner">⏳</span> Auto-formatting your data...
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="file-upload-section">
              <h4>Upload CSV File</h4>
              <div className="file-upload">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  id="csv-upload"
                  style={{ display: 'none' }}
                />
                <label htmlFor="csv-upload" className="file-upload-btn">
                  📤 Choose CSV File
                </label>
              </div>
              <div className="file-upload-help">
                <p>💡 <strong>Tip:</strong> Select a CSV file from your computer. Make sure it follows the template format.</p>
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <div className="import-errors">
              <h4>❌ Validation Errors</h4>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.length > 0 && (
            <div className="import-preview">
              <h4>👀 Preview ({preview.length} events)</h4>
              <div className="preview-table-container">
                <div className="preview-table-scroll">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Venue</th>
                        <th>Address</th>
                        <th>Category</th>
                        <th>Subcategory</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Format</th>
                        <th>Region</th>
                        <th>Cost</th>
                        <th>Reservations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.slice(0, 10).map((event, index) => (
                        <tr key={index}>
                          <td className="title-cell">{event.event_title || 'N/A'}</td>
                          <td className="description-cell">{event.description ? event.description.substring(0, 80) + '...' : 'N/A'}</td>
                          <td>{event.venue || 'N/A'}</td>
                          <td>{event.address || 'N/A'}</td>
                          <td>{event.category || 'N/A'}</td>
                          <td>{event.subcategory || 'N/A'}</td>
                          <td>{event.start_time || 'N/A'}</td>
                          <td>{event.end_time || 'N/A'}</td>
                          <td>{event.format || 'N/A'}</td>
                          <td>{event.region || 'N/A'}</td>
                          <td>{event.isReservationsNeeded ? 'Yes' : 'No'}</td>
                          <td>{event.isReservationsNeeded ? 'Required' : 'Not Required'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.length > 10 && (
                  <p className="preview-more">... and {preview.length - 10} more events</p>
                )}
              </div>
              
              {/* Admin Notes Section */}
              <div className="admin-notes-section">
                <h4>🔒 Admin Notes - Filtered Content</h4>
                {preview.slice(0, 5).map((event, index) => {
                  const hasAdminNotes = Object.values(event.adminNotes || {}).some(notes => notes.length > 0);
                  if (!hasAdminNotes) return null;
                  
                  return (
                    <div key={index} className="admin-notes-item">
                      <h5>Event: {event.event_title || 'Untitled'}</h5>
                      <div className="admin-notes-content">
                        {event.adminNotes?.advertisement?.length > 0 && (
                          <div className="admin-note-category">
                            <strong>📢 Advertisements:</strong>
                            <ul>
                              {event.adminNotes.advertisement.map((note, i) => (
                                <li key={i}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.adminNotes?.socialMedia?.length > 0 && (
                          <div className="admin-note-category">
                            <strong>📱 Social Media:</strong>
                            <ul>
                              {event.adminNotes.socialMedia.map((note, i) => (
                                <li key={i}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.adminNotes?.navigation?.length > 0 && (
                          <div className="admin-note-category">
                            <strong>🔗 Navigation:</strong>
                            <ul>
                              {event.adminNotes.navigation.map((note, i) => (
                                <li key={i}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.adminNotes?.promotional?.length > 0 && (
                          <div className="admin-note-category">
                            <strong>📧 Promotional:</strong>
                            <ul>
                              {event.adminNotes.promotional.map((note, i) => (
                                <li key={i}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.adminNotes?.legal?.length > 0 && (
                          <div className="admin-note-category">
                            <strong>⚖️ Legal:</strong>
                            <ul>
                              {event.adminNotes.legal.map((note, i) => (
                                <li key={i}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.adminNotes?.unrelatedEvents?.length > 0 && (
                          <div className="admin-note-category">
                            <strong>🎭 Unrelated Events:</strong>
                            <ul>
                              {event.adminNotes.unrelatedEvents.map((note, i) => (
                                <li key={i}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.adminNotes?.other?.length > 0 && (
                          <div className="admin-note-category">
                            <strong>📝 Other:</strong>
                            <ul>
                              {event.adminNotes.other.map((note, i) => (
                                <li key={i}>{note}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="import-footer">
          <button onClick={onCancel} className="btn-cancel">
            Cancel
          </button>
          <button 
            onClick={handleImport} 
            disabled={preview.length === 0 || errors.length > 0 || isProcessing}
            className="btn-import"
          >
            {isProcessing ? 'Importing...' : `Import ${preview.length} Events`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
